import { Button } from "@/components/ui/button";
import { Check, X, Printer, Package, Save } from "lucide-react";
import { ConfirmationDialog } from "@/components/atoms/ConfirmationDialog";
import type { CredentialRequest } from "../../types";
import { useCredentialPermissions } from "../../hooks/useCredentialPermissions";

interface RequestActionsBarProps {
  request: CredentialRequest | null;
  canEdit: boolean;
  ministerCount: number;
  isSubmitting: boolean;
  onSubmit: () => void;
  onApprove: () => void;
  onReject: () => void;
  onMarkPrinted: () => void;
  onMarkDelivered: () => void;
  // Admin edit mode
  isAdminEditMode?: boolean;
  hasUnsavedChanges?: boolean;
  onSaveChanges?: () => void;
  isSaving?: boolean;
  pendingChangesCount?: number;
}

/**
 * Molecule: Action buttons for request workflow
 */
export function RequestActionsBar({
  request,
  isSubmitting,
  onApprove,
  onReject,
  onMarkPrinted,
  onMarkDelivered,
  isAdminEditMode = false,
  hasUnsavedChanges = false,
  onSaveChanges,
  isSaving = false,
  pendingChangesCount = 0,
}: RequestActionsBarProps) {
  const { forRequest } = useCredentialPermissions();
  if (!request) return null;
  const perms = forRequest(request);

  // Determine button styling based on edit mode
  const workflowButtonVariant = isAdminEditMode ? "outline" : "default";

  return (
    <div className="mb-6 flex flex-wrap justify-end gap-2">
      {/* Submit button removed for DRAFT/REJECTED - now using save buttons in template */}
      
      {/* Save Changes button - show in admin edit mode */}
      {isAdminEditMode && onSaveChanges && (
        <Button
          onClick={onSaveChanges}
          disabled={!hasUnsavedChanges || isSaving || isSubmitting}
          variant="default"
          className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700"
        >
          <Save className="mr-2 size-4" />
          {isSaving
            ? "Guardando..."
            : hasUnsavedChanges
            ? `Guardar Cambios${pendingChangesCount > 0 ? ` (${pendingChangesCount})` : ""}`
            : "Sin Cambios"}
        </Button>
      )}
      
      {/* Workflow actions - each needs its own permission in the request's territory */}
      {(perms.canApprove || perms.canReject) && request.status === 'SUBMITTED' && (
        <>
          {perms.canApprove && (
          <ConfirmationDialog
            title="Confirmar Aprobación"
            description="¿Está seguro que desea aprobar esta solicitud de credenciales? Esta acción no se puede deshacer."
            confirmText="Aprobar"
            onConfirm={onApprove}
          >
            <Button 
              disabled={isSubmitting}
              variant={workflowButtonVariant}
              className={workflowButtonVariant === "default" ? "bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700" : ""}
            >
              <Check className="mr-2 size-4" />
              Aprobar
            </Button>
          </ConfirmationDialog>
          )}
          {perms.canReject && (
          <Button 
            onClick={onReject}
            disabled={isSubmitting}
            variant={isAdminEditMode ? "outline" : "destructive"}
          >
            <X className="mr-2 size-4" />
            Observar
          </Button>
          )}
        </>
      )}
      
      {perms.canMarkPrinted && request.status === 'APPROVED' && (
        <ConfirmationDialog
          title="Confirmar Impresión"
          description="¿Confirma que las credenciales han sido impresas? El estado cambiará a 'Impreso'."
          confirmText="Marcar como Impreso"
          onConfirm={onMarkPrinted}
        >
          <Button 
            disabled={isSubmitting}
            variant={workflowButtonVariant}
            className={workflowButtonVariant === "default" ? "bg-purple-600 hover:bg-purple-700 dark:bg-purple-600 dark:hover:bg-purple-700" : ""}
          >
            <Printer className="mr-2 size-4" />
            Marcar como Impreso
          </Button>
        </ConfirmationDialog>
      )}
      
      {perms.canMarkDelivered && request.status === 'PRINTED' && (
        <ConfirmationDialog
          title="Confirmar Entrega"
          description="¿Confirma que las credenciales han sido entregadas? El estado cambiará a 'Entregado' y finalizará el proceso."
          confirmText="Marcar como Entregado"
          onConfirm={onMarkDelivered}
        >
          <Button 
            disabled={isSubmitting}
            variant={workflowButtonVariant}
            className={workflowButtonVariant === "default" ? "bg-teal-600 hover:bg-teal-700 dark:bg-teal-600 dark:hover:bg-teal-700" : ""}
          >
            <Package className="mr-2 size-4" />
            Marcar como Entregado
          </Button>
        </ConfirmationDialog>
      )}
    </div>
  );
}
